import { DEFAULT_UI_SCALE, UI_SCALES, type UiScale } from '@shared/enums.js';

/**
 * Interface scale.
 *
 * The whole mechanism is one number: the root font size. Every size in the
 * application is already expressed in `rem` — Tailwind's `text-sm`, `h-7`,
 * `px-3`, `gap-2` and the rest are rem utilities — so changing the root
 * rescales the entire interface proportionally, with no per-component work and
 * nothing to keep in sync.
 *
 * That is why this file is small, and why Phase 6 did not rewrite hundreds of
 * class names. The things that DON'T follow the root are the exceptions worth
 * hunting: literal pixel dimensions, and inline styles that override a rem
 * utility. There were three of those in the whole client.
 *
 * Deliberately dependency-free so the reducer can be tested without a DOM.
 */

/** The browser default, and what 100% means. */
export const BASE_FONT_SIZE_PX = 16;

/** localStorage key for the pre-authentication mirror. */
export const UI_SCALE_STORAGE_KEY = 'leadforge.uiScale';

/** Root font size in pixels for a scale. 100 → 16, 125 → 20, 140 → 22.4. */
export function rootFontSizePx(scale: UiScale): number {
  return (BASE_FONT_SIZE_PX * scale) / 100;
}

/** The CSS value to put on the root element. */
export function rootFontSizeCss(scale: UiScale): string {
  return `${rootFontSizePx(scale)}px`;
}

/**
 * Coerce anything at all into a supported scale.
 *
 * The input can come from localStorage (which a user can edit, and which may
 * hold a value written by an older build), from a server response, or from a
 * `<select>`. None of them are trusted. Anything unrecognised reads as 100%,
 * because a wrong-but-usable interface beats a broken one.
 */
export function coerceUiScale(value: unknown): UiScale {
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;
  return UI_SCALES.find((scale) => scale === numeric) ?? DEFAULT_UI_SCALE;
}

/** True when the value is exactly one of the supported scales. */
export function isUiScale(value: unknown): value is UiScale {
  return UI_SCALES.some((scale) => scale === value);
}

// ---------------------------------------------------------------------------
// The localStorage mirror
//
// This is NOT the source of truth. Once a user is known, their server
// preference is. The mirror exists for the moment before that: the login page,
// and the first paint after a reload, where reading from the server would mean
// rendering at 100% and then jumping. It also survives logout, so the login
// page stays at the scale the person was just using.
// ---------------------------------------------------------------------------

/** Storage, if the browser has it and permits it. Null in a test or a sandbox. */
function storage(): Storage | null {
  try {
    // Private-mode Safari throws on access rather than returning null, and a
    // scale preference is not worth an exception at module load.
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readStoredUiScale(): UiScale {
  try {
    return coerceUiScale(storage()?.getItem(UI_SCALE_STORAGE_KEY));
  } catch {
    return DEFAULT_UI_SCALE;
  }
}

export function writeStoredUiScale(scale: UiScale): void {
  try {
    storage()?.setItem(UI_SCALE_STORAGE_KEY, String(scale));
  } catch {
    // A full or blocked quota must not break signing in.
  }
}

// ---------------------------------------------------------------------------
// Applying it
// ---------------------------------------------------------------------------

/**
 * Set the root font size. **The only place that touches it.**
 *
 * Scattering `document.documentElement.style.fontSize` through components is
 * how two of them end up disagreeing and the last render wins.
 */
export function applyUiScale(scale: UiScale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.fontSize = rootFontSizeCss(scale);
  // Exposed for CSS that genuinely needs the number — currently nothing, but a
  // scale-aware media query or container would read this rather than
  // recomputing it.
  document.documentElement.dataset.uiScale = String(scale);
}

/**
 * Apply the last known scale as early as possible.
 *
 * Called from the entry point before React renders, so the first paint is
 * already at the right size. Waiting for the auth query would mean the login
 * page appearing at 100% and jumping a moment later, which is exactly the
 * flash this is meant to avoid.
 */
export function applyStoredUiScale(): UiScale {
  const scale = readStoredUiScale();
  applyUiScale(scale);
  currentScale = scale;
  return scale;
}

// ---------------------------------------------------------------------------
// How the signed-in user's preference is fetched
//
// Here rather than inline in auth.tsx so the two decisions that matter can be
// asserted by a test without rendering anything.
// ---------------------------------------------------------------------------

/**
 * Cache key for a user's preferences, namespaced by user id.
 *
 * The id is part of the key so one account can never read another's cached
 * value — signing in as the VA must not show the admin's scale for the
 * fraction of a second before a fetch lands.
 */
export function preferenceQueryKey(userId: number | null | undefined): (string | number | null)[] {
  return ['auth', 'preferences', userId ?? null];
}

/**
 * Zero. The preference is stale the moment it is read.
 *
 * It was five minutes, which meant signing out and back into the SAME account
 * inside that window reused the cached value — so a scale changed on another
 * device, or by the owner on the machine itself, would be silently ignored on
 * the next sign-in. The Phase 6 rule is that the server preference is
 * authoritative once a user is known, and a cache that outlives a sign-in
 * contradicts it.
 *
 * This does NOT cause polling: `refetchOnWindowFocus` is off globally and the
 * query has no interval, so a stale entry is only refetched when the query
 * remounts or the user changes — which is exactly the moment it should be.
 */
export const PREFERENCE_STALE_TIME_MS = 0;

// ---------------------------------------------------------------------------
// The current scale, as one observable value
//
// Three things need to agree: the root font size, the localStorage mirror, and
// whatever the selector shows. Keeping them in sync by hand is how the menu
// ends up reading 100% while the interface is at 140% — so there is one value
// and one way to change it.
// ---------------------------------------------------------------------------

let currentScale: UiScale = DEFAULT_UI_SCALE;
const listeners = new Set<() => void>();

/** For useSyncExternalStore, and for tests. */
export function getUiScale(): UiScale {
  return currentScale;
}

export function subscribeToUiScale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Adopt a scale as the current one: apply it, remember it, tell everyone.
 *
 * Used both when a signed-in user's server preference arrives and when someone
 * picks a scale from the menu, so the mirror, the root element and the menu
 * cannot drift from one another.
 */
export function adoptUiScale(scale: UiScale): void {
  applyUiScale(scale);
  writeStoredUiScale(scale);
  if (currentScale === scale) return;
  currentScale = scale;
  for (const listener of listeners) listener();
}
